import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useApolloClient } from '@apollo/client';
import { format } from 'date-fns';

// Components
import ModalContainer from '../../containers/ModalContainer';
import { Typography } from '../../../display/Typography';
import { Loader } from '../../../inputs/Loader';
import InputText from '../../../inputs/InputText';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import { InputTextArea } from '../../../inputs/InputTextArea';
import { DateTime } from '../../../inputs/DateTime';
import { CheckBox } from '../../../inputs/CheckBox';
import { ArrowDirectionButton } from '../../../inputs/buttons/ArrowDirectionButton';
import { MainButton } from '../../../inputs/buttons/MainButton';
import SegmentList, {
  type SegmentRowNote,
} from '../../../../pages/main/surveillanceRoute/components/SegmentList';

// Hooks
import {
  useRoadDirectionLabels,
  useRoadNamesById,
} from '../../../../hooks/useRoadDirectionLabels';
import { useModalCloseLock } from '../../modalCloseLock';

// Data
import { GET_SEGMENT_STOP_SUGGESTIONS } from '../../../../gql/queries/surveillanceRoute';

// Utils
import { normalizePk } from '../../../../utils/transform';
import {
  directionNameKey,
  selectableSegmentCauses,
} from '../../../../utils/surveillanceRoute';
import { roadIdsInvolved } from '../../../../utils/roadStretches';
import {
  canSegmentTakeStop,
  draftsToInputs,
  manualDraft,
  planDrafts,
  preloadedStopCause,
  PreviewRowNote,
  retimeDraft,
  StopDraft,
  suggestionToDraft,
} from './draft';

// Types
import { ModalProps } from './types';
import type { SegmentStopSuggestion } from '../../../../types/surveillanceRoute';
import { WorkOrderDirection } from '../../../../types/workOrder';

// Styles
import {
  Body,
  BoxTitle,
  ButtonContainer,
  CardError,
  CloseButton,
  Container,
  ContainerLoader,
  DatesGrid,
  EmptyMessage,
  Field,
  FieldsGrid,
  InlineError,
  LilacBox,
  LinkBadge,
  LinkText,
  MainButtonStyled,
  Notice,
  NoticeIcon,
  PkFieldWrap,
  Section,
  SectionHeader,
  StopRow,
  Table,
  StopTableScroll,
  FormCell,
  FormCellBody,
  Td,
  TextAreaWrap,
  Th,
  Thead,
} from './styles';

const hm = (iso: string) =>
  new Date(iso).toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
  });
const dayHm = (iso: string) => format(new Date(iso), 'dd/MM HH:mm');
const pk = (km?: number | null, meter?: number | null) =>
  km == null ? '—' : `${km}+${String(meter ?? 0).padStart(3, '0')}`;

/** Columnas de la tabla de paradas (casilla + 6). */
const STOP_COLUMNS = 7;

/**
 * RegisterStopModal (spec 2026-10-06 § 7.2) — gestión registra, dentro de un
 * tramo CERRADO, las paradas que la patrulla no pausó, y el tramo se parte.
 *
 * Se abre desde la tabla de Tramos (enlace «Parada», mismas reglas de acceso
 * que «Corregir»). Al abrirse pide al servidor las paradas que el GPS crudo
 * del tramo deja ver; quien registra marca las que son, las retoca o añade una
 * a mano. La vista previa y los errores salen del espejo del plan
 * (`utils/segmentStopPlan.ts`) más las reglas de «Corregir» contra los
 * vecinos; el servidor revalida todo.
 *
 * 14-oct (feedback del usuario): modal ancho como los de tabla. Las paradas
 * van en una tabla (un `<tbody>` por parada) con los campos de «Corregir», y
 * «Así quedará» es la propia tabla de Tramos en solo lectura. Desde el 22/23-oct
 * cada parada marcada lleva su tarjeta (con su aviso) justo debajo de su fila,
 * la fila entera marca y desmarca, y las añadidas a mano se pueden quitar.
 */
export const RegisterStopModal = ({
  onClickClose,
  data,
}: ModalProps): JSX.Element => {
  const { t } = useTranslation([
    'pages',
    'common',
    'forms',
    'errors',
    'tables',
  ]);
  const theme = useTheme();
  const client = useApolloClient();
  const segment = data?.segment;
  // ¿Se puede crear la básica en este recorrido? Lo decide la página.
  const canChooseActionCause = Boolean(data?.canChooseActionCause);
  const segments = useMemo(() => data?.segments ?? [], [data?.segments]);

  const [drafts, setDrafts] = useState<StopDraft[]>([]);
  // Contador de las paradas a mano: su clave no puede salir del número de
  // filas, porque tras quitar una la siguiente repetiría la de otra.
  const manualCount = useRef(0);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(true);
  const [suggestionsFailed, setSuggestionsFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const closeLock = useModalCloseLock();
  // Revisión del 22-oct: en un tramo de menos de 3 min no cabe ninguna parada
  // (ver `manualStopTimes`). Se dice al abrir, en vez de dejar añadir una que
  // luego no se puede arreglar.
  const fitsStop = useMemo(
    () => !!segment && canSegmentTakeStop(segment),
    [segment]
  );

  const directionLabels = useRoadDirectionLabels(data?.roadId);
  const stretchRoadIds = useMemo(
    () => [...new Set((segment?.roadStretches ?? []).map((s) => s.roadId))],
    [segment?.roadStretches]
  );
  const roadNames = useRoadNamesById(stretchRoadIds);
  // «Así quedará» nombra las carreteras con el MISMO criterio que la tabla de
  // Tramos de la página (las del recorrido más las de los tramos): con solo
  // los trozos del tramo, uno viejo sin trozos salía con «-» (banco, 15-oct).
  const previewRoadIds = useMemo(
    () => roadIdsInvolved(data?.roadIds, segments),
    [data?.roadIds, segments]
  );
  const previewRoadNames = useRoadNamesById(previewRoadIds);

  useEffect(() => {
    if (!segment) return undefined;
    // Sin sitio para una parada, lo que enseñe el GPS no se puede registrar.
    if (!fitsStop) {
      setIsLoadingSuggestions(false);
      return undefined;
    }
    let alive = true;
    client
      .query<{ segmentStopSuggestions: SegmentStopSuggestion[] }>({
        query: GET_SEGMENT_STOP_SUGGESTIONS,
        variables: { segmentId: segment.id },
        fetchPolicy: 'network-only',
      })
      .then((result) => {
        if (!alive) return;
        const loaded = (result.data?.segmentStopSuggestions ?? []).map(
          (suggestion, i) => suggestionToDraft(suggestion, segment, i)
        );
        setDrafts(loaded);
      })
      .catch(() => {
        // Sin sugerencias se puede seguir: la parada se escribe a mano.
        if (alive) setSuggestionsFailed(true);
      })
      .finally(() => {
        if (alive) setIsLoadingSuggestions(false);
      });
    return () => {
      alive = false;
    };
  }, [client, segment, fitsStop]);

  const plan = useMemo(
    () => (segment ? planDrafts(segment, segments, drafts) : null),
    [segment, segments, drafts]
  );
  const anyEnabled = drafts.some((d) => d.enabled);
  const anyManual = drafts.some((d) => !d.fromGps);
  // Con alguna parada a mano, una columna más para «Quitar».
  const stopColumns = STOP_COLUMNS + (anyManual ? 1 : 0);

  const update = useCallback((key: string, patch: Partial<StopDraft>) => {
    setDrafts((current) =>
      current.map((d) => (d.key === key ? { ...d, ...patch } : d))
    );
  }, []);

  // Revisión del 23-oct (el usuario: «si marco luego no se desmarca»): lo que
  // se ve sale SOLO de `enabled`. Marcada = su tarjeta debajo de su fila;
  // desmarcada = sin tarjeta, fuera de «Así quedará» y de lo que se guarda.
  // Lo escrito se conserva por si se vuelve a marcar. Pulsar la fila hace lo
  // mismo que la casilla.
  const toggleRow = useCallback((key: string) => {
    setDrafts((current) =>
      current.map((d) => (d.key === key ? { ...d, enabled: !d.enabled } : d))
    );
  }, []);

  // Solo una parada a mano, que aún no existe en el servidor: lo registrado
  // no está en esta tabla y no se toca.
  const removeManual = useCallback((key: string) => {
    setDrafts((current) => current.filter((d) => d.fromGps || d.key !== key));
  }, []);

  // Las horas pueden cambiar la forma de la parada: se recalcula con ellas.
  const retime = useCallback(
    (key: string, patch: { startDate?: string; endDate?: string }) => {
      if (!segment) return;
      setDrafts((current) =>
        current.map((d) =>
          d.key === key
            ? retimeDraft(segment, d, patch, { canChooseActionCause })
            : d
        )
      );
    },
    [canChooseActionCause, segment]
  );

  const addManual = useCallback(() => {
    if (!segment) return;
    const draft = manualDraft(segment, manualCount.current, drafts);
    if (!draft) return;
    manualCount.current += 1;
    setDrafts((current) => [...current, draft]);
  }, [segment, drafts]);
  // Sin hueco libre donde quepa otra parada, el botón se apaga (29-oct).
  const canAddManual = useMemo(
    () => !!segment && manualDraft(segment, 0, drafts) !== null,
    [segment, drafts]
  );

  // Misma regla que «Cambiar causa» (15-oct): sin poder crear la básica en
  // este recorrido (sala, siempre) no se ofrece «Actuación» —una pieza nueva
  // quedaría pendiente de una básica que nadie puede crear—, salvo la que se
  // le precargó a la parada en su forma actual (la del cierre, en una parada
  // al final), que no crea nada nuevo. Solo esa (revisión M4): si la parada
  // cambia de forma, `retimeDraft` vacía la que ya no vale. Por parada: cada
  // marcada tiene su tarjeta (23-oct).
  const causeOptionsFor = (draft: StopDraft) => {
    const allowedCauses = new Set(
      selectableSegmentCauses(
        canChooseActionCause,
        segment ? preloadedStopCause(segment, draft) : null
      )
    );
    return [
      { value: 'basic', label: t('surveillance.pause.causes.basic') },
      { value: 'action', label: t('surveillance.pause.causes.action') },
      {
        value: 'external-call',
        label: t('surveillance.pause.causes.external-call'),
      },
    ].filter((option) => allowedCauses.has(option.value));
  };
  const roadOptions = stretchRoadIds.map((id) => ({
    value: id,
    label: roadNames[id] || id,
  }));
  // Mismo mapeo que «Corregir» y los modales del cronómetro.
  const directionValue: Record<string, string> = {
    top: WorkOrderDirection.TOP,
    down: WorkOrderDirection.DOWN,
  };

  const onSubmit = useCallback(async () => {
    if (!data?.onConfirm || !plan || plan.error || isSubmitting) return;
    setIsSubmitting(true);
    // Mientras viaja no se cierra: si falla, lo escrito sigue aquí (QA 15-oct).
    closeLock.lock();
    let saved = false;
    try {
      await data.onConfirm(
        draftsToInputs(drafts, (direction) =>
          t(
            directionNameKey(direction) ??
              'pages:surveillance.directionUnspecified'
          )
        )
      );
      saved = true;
    } catch {
      // El toast del error ya lo emitió el hook; el modal sigue abierto.
    } finally {
      closeLock.unlock();
      setIsSubmitting(false);
    }
    if (saved) onClickClose();
  }, [closeLock, data, drafts, isSubmitting, onClickClose, plan, t]);

  // Cabecera: el tramo, con el Nº que tiene en la tabla de Tramos.
  const number = useMemo(() => {
    if (!segment) return null;
    const ordered = [...segments].sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );
    const i = ordered.findIndex((s) => s.id === segment.id);
    return i >= 0 ? i + 1 : null;
  }, [segment, segments]);
  // «Así quedará» (29-oct; en árbol, 30-oct): cómo se pinta cada fila.
  const previewRowNotes = useMemo(() => {
    const notes: Record<string, SegmentRowNote> = {};
    if (!plan) return notes;
    const key = 'pages:surveillance.registerStop';
    // Lo que dice la «Causa» de una parada pegada a un borde: de quién es la
    // pausa que alarga y, si la fila dura más que la parada de arriba, por qué.
    const stopNote = (note: PreviewRowNote): string | undefined => {
      const minutes = note.extraMinutes ?? 0;
      if (note.shiftEdge) {
        return note.edge === 'start'
          ? t(`${key}.previewShiftStart`)
          : t(`${key}.previewShiftEnd`);
      }
      // La del inicio lo dice siempre (pasa a OTRO tramo); la del final, solo
      // si la fila dura más que la parada de arriba.
      if (minutes > 0) {
        return t(`${key}.previewPauseOfMoving`, {
          number: note.pauseOf,
          stopped: note.stoppedMinutes,
          minutes,
        });
      }
      return note.edge === 'start'
        ? t(`${key}.previewPauseOf`, { number: note.pauseOf })
        : undefined;
    };
    // El padre y las paradas no gastan número: el de cada tramo cuenta solo
    // tramos (el de después dice cuál era si cambia).
    let newNumber = plan.previewFirstNumber - 1;
    plan.previewSegments.forEach((row) => {
      const note = plan.rowNotes[row.id];
      if (!note) return;
      const tree = note.depth
        ? { depth: note.depth, last: note.last, through: note.through }
        : undefined;
      if (note.kind === 'parent') {
        notes[row.id] = {
          kind: 'parent',
          muted: true,
          number: note.number,
          badge: t(`${key}.previewNow`),
        };
      } else if (note.kind === 'stop') {
        notes[row.id] = {
          kind: 'stop',
          tree,
          label: t(`${key}.previewStopLabel`),
          causeNote: stopNote(note),
        };
      } else if (note.kind === 'piece') {
        newNumber += 1;
        notes[row.id] = { kind: 'piece', tree };
      } else {
        newNumber += 1;
        const moved = note.wasNumber != null && newNumber !== note.wasNumber;
        notes[row.id] = {
          kind: 'context',
          muted: true,
          badge: moved
            ? t(`${key}.previewWas`, { number: note.wasNumber })
            : undefined,
        };
      }
    });
    return notes;
  }, [plan, t]);
  // Cabecera (29-oct): qué paradas del GPS vienen marcadas de entrada (las de
  // UNA sola OT candidata, ver `suggestionToDraft`) y por qué, o que ninguna.
  const autoMarkedHint = useMemo(() => {
    const fromGps = drafts.filter((d) => d.fromGps);
    if (fromGps.length === 0) return '';
    const marked = fromGps.filter((d) => d.candidatesCount === 1);
    if (marked.length === 0) {
      return t('pages:surveillance.registerStop.autoMarkedHintNone');
    }
    if (marked.length === 1) {
      return t('pages:surveillance.registerStop.autoMarkedHintOne', {
        start: hm(marked[0].startDate),
        end: hm(marked[0].endDate),
        roid: marked[0].candidateRoid ?? '',
      });
    }
    return t('pages:surveillance.registerStop.autoMarkedHintMany');
  }, [drafts, t]);
  const cause = segment?.pauseCause
    ? t(`pages:surveillance.pause.causes.${segment.pauseCause}`, {
        defaultValue: segment.pauseCause,
      })
    : '';
  const title = t('pages:surveillance.registerStop.title');
  const heading = segment
    ? `${title} ·${number ? ` Nº ${number} ·` : ''} ${dayHm(
        segment.startDate
      )}–${segment.endDate ? hm(segment.endDate) : '—'} · ${pk(
        segment.pkInitKm,
        segment.pkInitMeter
      )} → ${pk(segment.pkEndKm, segment.pkEndMeter)}${
        cause ? ` · ${cause}` : ''
      }`
    : title;

  // El aviso de una parada, dentro de su tarjeta. «Indica la causa de la
  // parada» no sale: la tarjeta ya enseña la causa sin elegir y el botón de
  // registrar sigue apagado (revisión del 23-oct, el usuario: «sobra»).
  const cardError = (draft: StopDraft) =>
    draft.enabled &&
    plan?.error &&
    plan.errorDraftKey === draft.key &&
    plan.error !== 'errors:backend.SR_STOP_CAUSE_REQUIRED'
      ? `${
          plan.error === 'errors:backend.SR_STOP_OVERLAP' ||
          plan.error === 'errors:backend.SR_STOP_COVERS_SEGMENT'
            ? t('pages:surveillance.registerStop.stopsOverlap')
            : t(plan.error)
        }${
          plan.error === 'errors:backend.SR_SEGMENT_PK_DIRECTION_MISMATCH' &&
          draft.shape === 'middle'
            ? ` ${t('pages:surveillance.registerStop.directionHint')}`
            : ''
        }`
      : null;

  // La tarjeta de una parada marcada (revisiones del 22 y 23-oct): va DENTRO
  // de la tabla, justo debajo de su fila, como pidió el usuario («la card de
  // rellenar la causa, que aparezca debajo de la fila»). Cada marcada lleva
  // la suya, también la añadida a mano; su aviso va dentro, sin fila propia.
  const renderStopForm = (activeDraft: StopDraft) => {
    if (!segment) return null;
    const error = cardError(activeDraft);
    return (
      <LilacBox data-testid="register-stop-form">
        <Section>
          <Typography size="M" variant="semiBold">
            {`${hm(activeDraft.startDate)}–${hm(activeDraft.endDate)}`}
          </Typography>
          {error && (
            <CardError size="S" data-testid="register-stop-card-error">
              {error}
            </CardError>
          )}
          <DatesGrid>
            {/* Un `key` por parada: `DateTime` guarda por dentro la hora
                      con la que se montó, y al pasar a otra parada «1» en la
                      hora traía los minutos de la anterior (QA 15-oct). */}
            <DateTime
              key={`arrival-${activeDraft.key}`}
              label={t('pages:surveillance.registerStop.arrival')}
              labelHour={t('pages:surveillance.registerStop.arrivalHour')}
              value={activeDraft.startDate}
              initialDate={activeDraft.startDate}
              fromDate={new Date(segment.startDate)}
              toDate={segment.endDate ? new Date(segment.endDate) : new Date()}
              onChangeDate={(v) =>
                v && retime(activeDraft.key, { startDate: v })
              }
            />
            <DateTime
              key={`departure-${activeDraft.key}`}
              label={t('pages:surveillance.registerStop.departure')}
              labelHour={t('pages:surveillance.registerStop.departureHour')}
              value={activeDraft.endDate}
              initialDate={activeDraft.endDate}
              fromDate={new Date(segment.startDate)}
              toDate={segment.endDate ? new Date(segment.endDate) : new Date()}
              onChangeDate={(v) => v && retime(activeDraft.key, { endDate: v })}
            />
          </DatesGrid>
          <FieldsGrid>
            <Field>
              <Typography size="S">
                {t('pages:surveillance.registerStop.pk')}
              </Typography>
              <PkFieldWrap>
                <InputText
                  typeInput="normal"
                  inputSize="S"
                  value={activeDraft.pk}
                  placeholder={t('common:pkExample')}
                  onChange={(e) =>
                    update(activeDraft.key, { pk: e.target.value })
                  }
                  onBlur={() => {
                    const normalized = normalizePk(activeDraft.pk);
                    if (normalized) {
                      update(activeDraft.key, { pk: normalized });
                    }
                  }}
                  data-testid="register-stop-pk"
                />
              </PkFieldWrap>
            </Field>
            {roadOptions.length > 1 && (
              <Field>
                <Typography size="S">
                  {t('pages:surveillance.registerStop.road')}
                </Typography>
                <DropDownInput
                  id={`register-stop-road-${activeDraft.key}`}
                  inputSize="L"
                  typeDropDown="Default"
                  placeholder={t('common:labels.select')}
                  options={roadOptions}
                  // '' y no `undefined`, como la causa (QA T1.5+a): con
                  // `undefined` la caja conservaba la carretera de la
                  // parada anterior en una que no tiene.
                  selectedOption={activeDraft.roadId ?? ''}
                  onChangeSelected={(option) =>
                    update(activeDraft.key, {
                      roadId: (option?.value as string) ?? null,
                    })
                  }
                />
              </Field>
            )}
          </FieldsGrid>
          <Field>
            <Typography size="S">
              {t('pages:surveillance.registerStop.cause')}
            </Typography>
            <DropDownInput
              id={`register-stop-cause-${activeDraft.key}`}
              inputSize="L"
              typeDropDown="Default"
              // Bloqueada («al inicio»: la causa no se elige aquí) no
              // invita a elegir: «—» y no «Seleccionar» (QA 15-oct).
              placeholder={
                activeDraft.shape === 'start' ? '—' : t('common:labels.select')
              }
              options={causeOptionsFor(activeDraft)}
              // '' y no `undefined` (QA T1.5+a, 15-oct): `DropDownInput`
              // solo se vacía con '' o null; con `undefined` conserva la
              // etiqueta de antes, y la caja enseñaba una causa que el
              // borrador ya había vaciado al cambiar de forma.
              selectedOption={activeDraft.pauseCause ?? ''}
              isDisabled={activeDraft.shape === 'start'}
              onChangeSelected={(option) =>
                update(activeDraft.key, {
                  pauseCause: (option?.value as string) ?? '',
                })
              }
              data-testid="register-stop-cause"
            />
            {activeDraft.shape === 'start' && (
              <Typography size="S" colorText="greyDark">
                {/* En el Nº1 no hay tramo anterior del que tomar la
                          causa: esos minutos salen del turno (QA 15-oct). */}
                {/* 29-oct (el usuario no lo entendía): con la hora y el PK
                    en los que empezará el tramo y de qué tramo es la causa. */}
                {t(
                  number === 1
                    ? 'pages:surveillance.registerStop.startShapeHelpFirst'
                    : 'pages:surveillance.registerStop.startShapeHelp',
                  {
                    number,
                    previous: number != null ? number - 1 : '',
                    time: hm(activeDraft.endDate),
                    pk: activeDraft.pk,
                  }
                )}
              </Typography>
            )}
            {activeDraft.shape === 'end' && (
              <Typography size="S" colorText="greyDark">
                {/* 30-oct: con paradas en medio marcadas, el que acaba en
                    esta es el último trozo, que va tantos Nº detrás como
                    paradas en medio haya. */}
                {t('pages:surveillance.registerStop.endShapeHelp', {
                  number:
                    number != null
                      ? number +
                        drafts.filter((d) => d.enabled && d.shape === 'middle')
                          .length
                      : number,
                  time: hm(activeDraft.startDate),
                  pk: activeDraft.pk,
                })}
              </Typography>
            )}
          </Field>
          {activeDraft.shape !== 'start' && (
            <Field>
              <Typography size="S">
                {t('surveillance.pauseCauseDetail')}
              </Typography>
              <TextAreaWrap>
                <InputTextArea
                  value={activeDraft.pauseCauseDetail}
                  onChange={(e) =>
                    update(activeDraft.key, {
                      pauseCauseDetail: e.target.value,
                    })
                  }
                  placeholder={t(
                    'pages:surveillance.registerStop.detailPlaceholder'
                  )}
                  disabled={false}
                />
              </TextAreaWrap>
            </Field>
          )}
          {activeDraft.shape === 'middle' && (
            <Field>
              <Typography size="S">
                {t('pages:surveillance.registerStop.directionAfter')}
              </Typography>
              <ArrowDirectionButton
                directions={['down', 'top']}
                // Un grupo de radios POR PARADA: con el mismo `name`, el
                // sentido de una parada pisaba el de otra. Con `key`
                // propio el componente se monta de nuevo al cambiar de
                // parada y enseña el sentido de ESA.
                key={`direction-${activeDraft.key}`}
                name={`direction-${activeDraft.key}`}
                defaultValue={activeDraft.directionAfter ?? segment.direction}
                directionLabels={directionLabels}
                onChangeDirection={(dir) => {
                  const value = directionValue[dir] ?? dir;
                  update(activeDraft.key, {
                    directionAfter: value === segment.direction ? null : value,
                  });
                }}
              />
            </Field>
          )}
        </Section>
      </LilacBox>
    );
  };

  return (
    <ModalContainer onClickClose={onClickClose} fill={theme.colors.greyBoard}>
      <Container role="dialog" aria-modal="true" aria-label={title}>
        {isSubmitting && (
          <ContainerLoader>
            <Loader />
          </ContainerLoader>
        )}
        <Body $isLoading={isSubmitting}>
          <LilacBox>
            <BoxTitle component="h2" variant="semiBold" size="M">
              {heading}
            </BoxTitle>
            <Typography size="S" colorText="greyDark">
              {t('pages:surveillance.registerStop.intro')}
            </Typography>
            {autoMarkedHint && (
              <Typography
                size="S"
                colorText="greyDark"
                data-testid="register-stop-auto-hint"
              >
                {autoMarkedHint}
              </Typography>
            )}
          </LilacBox>

          {/* Un tramo demasiado corto: solo el aviso, en naranja como los
              demás avisos. Ni el título de las paradas del GPS ni un botón
              apagado, que no sirven para nada (revisión del 22-oct). */}
          {!fitsStop && (
            <Notice role="alert" data-testid="register-stop-too-short">
              <NoticeIcon aria-hidden="true">⚠</NoticeIcon>
              <span>{t('pages:surveillance.registerStop.tooShort')}</span>
            </Notice>
          )}
          {fitsStop && (
            <Section>
              <SectionHeader>
                <Typography size="M" variant="semiBold">
                  {t('pages:surveillance.registerStop.detected')}
                </Typography>
                <MainButton
                  type="button"
                  variant="secondary"
                  text={t('pages:surveillance.registerStop.addManual')}
                  onClick={addManual}
                  disabled={isSubmitting || !segment || !canAddManual}
                  data-testid="register-stop-add-manual"
                />
              </SectionHeader>
              {isLoadingSuggestions && <Loader />}
              {/* Solo con la tabla vacía: con una parada ya añadida a mano,
                «Puedes añadirla a mano» sobra (revisión del 22-oct). */}
              {!isLoadingSuggestions && drafts.length === 0 && (
                <EmptyMessage data-testid="register-stop-no-suggestions">
                  {t(
                    suggestionsFailed
                      ? 'pages:surveillance.registerStop.noGps'
                      : 'pages:surveillance.registerStop.noStops'
                  )}
                </EmptyMessage>
              )}
              {drafts.length > 0 && (
                <StopTableScroll>
                  <Table>
                    <Thead>
                      <tr>
                        <Th />
                        <Th>{t('pages:surveillance.registerStop.arrival')}</Th>
                        <Th>
                          {t('pages:surveillance.registerStop.departure')}
                        </Th>
                        <Th>{t('forms:timeManagement.headers.duration')}</Th>
                        <Th>{t('pages:surveillance.registerStop.pk')}</Th>
                        <Th>
                          {t('pages:surveillance.registerStop.shapeHeader')}
                        </Th>
                        <Th>{t('pages:surveillance.linkedExternalColumn')}</Th>
                        {anyManual && <Th />}
                      </tr>
                    </Thead>
                    {drafts.map((draft) => (
                      <tbody
                        key={draft.key}
                        data-testid="register-stop-suggestion"
                      >
                        <StopRow
                          $selected={draft.enabled}
                          onClick={() => toggleRow(draft.key)}
                        >
                          {/* La celda entera pasa el toque a la fila (antes el
                            hueco alrededor de la casilla no hacía nada); la
                            casilla corta el suyo para no marcar dos veces. */}
                          <Td>
                            <CheckBox
                              indeterminate={false}
                              checked={draft.enabled}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleRow(draft.key)}
                              data-testid="register-stop-toggle"
                            />
                          </Td>
                          <Td>{hm(draft.startDate)}</Td>
                          <Td>{hm(draft.endDate)}</Td>
                          <Td>
                            {/* Con la salida antes que la llegada no hay
                                duración que enseñar (salía «-10 min»): lo
                                explica el aviso de su fila. */}
                            {draft.minutes != null && draft.minutes >= 0
                              ? t('pages:surveillance.registerStop.minutes', {
                                  minutes: draft.minutes,
                                })
                              : '—'}
                          </Td>
                          <Td>
                            {draft.pk ||
                              (draft.fromGps && !draft.onRoad
                                ? t('pages:surveillance.registerStop.offRoad')
                                : '—')}
                          </Td>
                          <Td>
                            {draft.shape !== 'covers'
                              ? t(
                                  `pages:surveillance.registerStop.shape.${draft.shape}`
                                )
                              : '—'}
                          </Td>
                          <Td>
                            {draft.candidateRoid &&
                            draft.candidatesCount === 1 ? (
                              <LinkBadge data-testid="register-stop-will-link">
                                {t('pages:surveillance.registerStop.willLink', {
                                  roid: draft.candidateRoid,
                                })}
                              </LinkBadge>
                            ) : (
                              '—'
                            )}
                          </Td>
                          {anyManual && (
                            <Td onClick={(e) => e.stopPropagation()}>
                              {draft.fromGps ? (
                                '—'
                              ) : (
                                <LinkText
                                  type="button"
                                  onClick={() => removeManual(draft.key)}
                                  disabled={isSubmitting}
                                  data-testid="register-stop-remove"
                                >
                                  {t(
                                    'pages:surveillance.registerStop.removeManual'
                                  )}
                                </LinkText>
                              )}
                            </Td>
                          )}
                        </StopRow>
                        {/* La tarjeta, con su aviso dentro: con varias
                            marcadas, un mensaje suelto al pie no dice cuál
                            tocar. */}
                        {draft.enabled && (
                          <tr>
                            <FormCell colSpan={stopColumns}>
                              <FormCellBody>
                                {renderStopForm(draft)}
                              </FormCellBody>
                            </FormCell>
                          </tr>
                        )}
                      </tbody>
                    ))}
                  </Table>
                </StopTableScroll>
              )}
            </Section>
          )}

          {/* Si solo falta la causa, se ve igual: así se entiende por qué no
              se puede registrar (29-oct). */}
          {plan && (!plan.error || plan.causeMissing) && plan.pieces.length > 0 && (
            <Section data-testid="register-stop-preview">
              <Typography size="M" variant="semiBold">
                {t('pages:surveillance.registerStop.preview')}
              </Typography>
              <Typography
                size="S"
                colorText="greyDark"
                data-testid="register-stop-preview-intro"
              >
                {plan.pieces.length > 1
                  ? t('pages:surveillance.registerStop.previewIntroSplit', {
                      number,
                      count: plan.pieces.length,
                    })
                  : t('pages:surveillance.registerStop.previewIntroTrim', {
                      number,
                    })}
              </Typography>
              <SegmentList
                segments={plan.previewSegments}
                firstNumber={plan.previewFirstNumber}
                roadNamesById={previewRoadNames}
                missingCauseIds={plan.missingCauseIds}
                rowNotes={previewRowNotes}
                keepOrder
              />
              {plan.shortensShift && (
                <Notice role="alert">
                  <NoticeIcon aria-hidden="true">⚠</NoticeIcon>
                  <span>
                    {t('pages:surveillance.registerStop.shortensShift')}
                  </span>
                </Notice>
              )}
            </Section>
          )}

          {plan?.error && anyEnabled && !plan.errorDraftKey && (
            <InlineError size="S" data-testid="register-stop-error">
              {t(plan.error)}
            </InlineError>
          )}

          <ButtonContainer>
            <CloseButton
              type="button"
              text={t('common:buttons.back')}
              variant="secondary"
              onClick={onClickClose}
              disabled={isSubmitting}
            />
            <MainButtonStyled
              type="button"
              text={t('pages:surveillance.registerStop.save')}
              onClick={() => void onSubmit()}
              disabled={isSubmitting || !plan || !!plan.error}
              data-testid="register-stop-confirm"
            />
          </ButtonContainer>
        </Body>
      </Container>
    </ModalContainer>
  );
};

export default RegisterStopModal;
